/**
 * Cross-tab "this user is actively present" signal.
 *
 * Why this exists: the idle logout in `IdleLogoutProvider` counts only raw
 * DOM input (mousemove / keydown / scroll / touch). A live video call
 * produces none of those, so an hour on camera looked identical to an
 * abandoned tab and the lockout fired at the end of a 60-minute session.
 *
 * Why a BroadcastChannel rather than the `storage` event used by
 * `crossTabAuth`: tokens default to `sessionStorage` (both login forms
 * default `rememberMe` to false), which is per-tab, so storage events never
 * cross tabs on the default path. BroadcastChannel is origin-scoped, so it
 * reaches the sibling tab that `openSessionCallTab` spawns for staff calls,
 * whose sessionStorage is a copy rather than the same store.
 *
 * The payload carries the publisher's user id and consumers are expected to
 * match it against their own signed-in user. On a shared browser two
 * different accounts can hold two tabs, and one account's call must not
 * hold the other's session open.
 */
export const PRESENCE_CHANNEL_NAME = 'elixaa_presence';

interface PresenceMessage {
  userId: string;
}

type PresenceListener = (userId: string) => void;

const listeners = new Set<PresenceListener>();

let channel: BroadcastChannel | null = null;
let channelResolved = false;

function notifyLocal(userId: string): void {
  listeners.forEach((listener) => {
    listener(userId);
  });
}

/**
 * Lazily open the channel, tolerating environments without BroadcastChannel
 * (older Safari, and any SSR/test context that omits it). Resolution is
 * cached so repeated publishes don't re-probe.
 */
function getChannel(): BroadcastChannel | null {
  if (channelResolved) return channel;
  channelResolved = true;
  if (typeof globalThis.BroadcastChannel === 'undefined') return null;

  // Constructing can throw on an opaque origin (sandboxed iframe, some
  // embedded webviews). Subscribers run inside the IdleLogoutProvider effect
  // that wraps the whole router, so an escaping throw there blanks the app.
  // Losing the cross-tab hop degrades to same-tab-only, which still fixes the
  // client call case; crashing the app does not.
  try {
    const opened = new BroadcastChannel(PRESENCE_CHANNEL_NAME);
    opened.onmessage = (event: MessageEvent) => {
      const data = event.data as PresenceMessage | null;
      if (!data?.userId) return;
      notifyLocal(data.userId);
    };
    channel = opened;
  } catch {
    channel = null;
  }
  return channel;
}

/**
 * Announce that the signed-in user is present. Notifies this tab's own
 * subscribers directly, since a BroadcastChannel never echoes to the
 * context that posted the message.
 */
export function publishPresence(userId: string): void {
  // Local first, so this tab's own idle timer is reset even if the hop out
  // to sibling tabs fails. postMessage throws InvalidStateError on a channel
  // the browser closed under us (bfcache eviction, page teardown).
  notifyLocal(userId);
  try {
    getChannel()?.postMessage({ userId } satisfies PresenceMessage);
  } catch {
    /* sibling tabs miss this ping; this tab is already reset */
  }
}

/** Subscribe to presence from this tab or any sibling tab. */
export function subscribePresence(listener: PresenceListener): () => void {
  getChannel();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
